import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, FileTextIcon, UploadIcon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { SLOT_CLASSES, SlotClass, selectionRationale } from '../../data/demo';
const MIX_SUMMARY = (slots: {
  klass: SlotClass;
}[]) => SLOT_CLASSES.map(k => ({
  k,
  n: slots.filter(s => s.klass === k).length
})).filter(c => c.n > 0).map(c => `${c.k} ×${c.n}`).join(' · ');
export function Configure() {
  const {
    mode,
    setMode,
    candidateCount,
    setCandidateCount,
    slots,
    setSlotClass,
    resetSlots,
    cvName,
    setCvName
  } = useDemo();
  const [editing, setEditing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [rationaleStep, setRationaleStep] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const showRationale = mode === 'single' && Boolean(cvName);
  useEffect(() => {
    if (!showRationale) {
      setRationaleStep(0);
      return;
    }
    if (rationaleStep >= selectionRationale.length) return;
    const t = window.setTimeout(() => setRationaleStep(s => s + 1), rationaleStep === 0 ? 350 : 250);
    return () => window.clearTimeout(t);
  }, [showRationale, rationaleStep]);

  // Selection visibly responds to the CV: matched scenarios settle to the top as rows land.
  const orderedSlots = useMemo(() => {
    if (!showRationale) return slots;
    const landed = selectionRationale.slice(0, rationaleStep).map(r => r.slotId).filter((id): id is string => Boolean(id));
    if (landed.length === 0) return slots;
    const matched = landed.map(id => slots.find(s => s.id === id)).filter((s): s is (typeof slots)[number] => Boolean(s));
    const rest = slots.filter(s => !landed.includes(s.id));
    return [...matched, ...rest];
  }, [slots, showRationale, rationaleStep]);
  const choose = (m: 'cohort' | 'single') => {
    setMode(m);
    if (m === 'single') setCandidateCount(1);else if (candidateCount < 2) setCandidateCount(3);
  };
  const attemptChange = (slotId: string, klass: SlotClass) => {
    const accepted = setSlotClass(slotId, klass);
    setRefusal(accepted ? null : 'Below the clean-case floor. An assessment that only contains errors measures paranoia, not judgement.');
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-455" data-mp="26xiffl6re7io">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-456" data-mp="26z20rq79v8rb">
        <LenticMark data-id="element-457" data-mp="226inkuchyfn1" />
        <BackLink to="/demo/analysis" label="Back to analysis" data-id="element-458" data-mp="2257hgnvkwvsr" />
      </div>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-8 py-10" data-id="element-459" data-mp="1egekqp74ujx3">
        <p className="eyebrow text-brand" data-id="element-460" data-mp="19icxds2obyft">Configuration</p>
        <h1 className="mt-3 text-[28px] font-semibold tracking-tight text-ink" data-id="element-461" data-mp="19huvaetbypzz">
          Confirm the mode and the mix.
        </h1>
        <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted" data-id="element-462" data-mp="3pw6pas6ong7l">
          This is the one screen where the choices are yours. Everything after it is the system’s
          discipline.
        </p>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-id="element-463" data-mp="3g63t6j49lm51">
          <section data-id="element-464" data-mp="7qtl8udp1mpc">
            <p className="eyebrow text-muted" data-id="element-465" data-mp="7qadu7lo6gpk">Mode</p>
            <div className="mt-4 space-y-4" data-id="element-466" data-mp="7oh9iyyj48kq">
              <button type="button" onClick={() => choose('cohort')} className={`w-full rounded-sm border bg-white p-5 text-left transition-all duration-150 ease-settle ${mode === 'cohort' ? 'border-brand opacity-100 ring-1 ring-brand/40' : 'border-rule opacity-60 hover:opacity-100'}`} data-id="element-467" data-mp="cpwu6pwsod2l">
                <div className="flex items-center justify-between" data-id="element-468" data-mp="2nj9c7w6np4wb">
                  <h2 className="text-[15px] font-semibold text-ink" data-id="element-469" data-mp="33p44wzgr2jcm">Cohort</h2>
                  {mode === 'cohort' && <span className="font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-470" data-mp="2yxcgauh5mnp4">
                      selected
                    </span>}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted" data-id="element-471" data-mp="2p1j4xef6908x">
                  One locked instrument, {Math.max(2, candidateCount)} candidates, comparable
                  results.
                </p>
                <p className="mt-3 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-472" data-mp="3hmj0klxljnd1">
                  Library version pins when you proceed and holds until the requisition closes.
                </p>
              </button>

              <button type="button" onClick={() => choose('single')} className={`w-full rounded-sm border bg-white p-5 text-left transition-all duration-150 ease-settle ${mode === 'single' ? 'border-brand ring-1 ring-brand/40' : 'border-rule hover:border-muted/50'}`} data-id="element-473" data-mp="21a3zhcs3m2tg">
                <div className="flex items-center justify-between" data-id="element-474" data-mp="gq8m5vo7avuf">
                  <h2 className="text-[15px] font-semibold text-ink" data-id="element-475" data-mp="lqmjojs6ene7">Single candidate</h2>
                  {mode === 'single' && <span className="font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-476" data-mp="qk7g47e4f9ba">
                      selected
                    </span>}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted" data-id="element-477" data-mp="10i5732nd7k7l">
                  A tailored probe for one person — exec diligence, contractor verification.
                  Comparability is moot by design.
                </p>

                {mode === 'single' && <div className="mt-4" data-id="element-478" data-mp="36b3h2qvml23a">
                    {cvName ? <span className="inline-flex items-center gap-2 rounded-sm border border-rule bg-paper px-3 py-2 font-mono-num text-[11px] text-ink" data-id="element-479" data-mp="3b9523hcm3bhe">
                        <FileTextIcon className="h-3.5 w-3.5 text-brand" data-id="element-480" data-mp="df1xy1kq9aab" />
                        {cvName} · parsed
                        <button type="button" aria-label="Remove CV" onClick={e => {
                    e.stopPropagation();
                    setCvName(null);
                  }} className="text-muted transition-colors hover:text-ink" data-id="element-481" data-mp="retk4pledwze">
                          <XIcon className="h-3 w-3" data-id="element-482" data-mp="1a5kofcqj2x6x" />
                        </button>
                      </span> : <div role="button" tabIndex={0} onClick={e => {
                  e.stopPropagation();
                  fileRef.current?.click();
                }} onKeyDown={e => {
                  if (e.key === 'Enter') fileRef.current?.click();
                }} className="flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-dashed border-rule bg-paper px-4 py-6 text-[13px] text-muted transition-colors duration-150 ease-settle hover:border-brand/60 hover:text-ink" data-id="element-483" data-mp="163zdvy1guuzw">
                        <UploadIcon className="h-4 w-4" data-id="element-484" data-mp="1fxvazinq2bxd" />
                        Upload CV
                      </div>}
                    <input ref={fileRef} type="file" className="hidden" onChange={e => {
                  if (e.target.files?.[0]) setCvName('CV-0417');
                }} data-id="element-485" data-mp="161a5kw7siyt0" />
                    <p className="mt-3 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-486" data-mp="1125vm3d1kx1m">
                      CV tailors selection and surface from the gated library. It never generates
                      content.
                    </p>
                  </div>}
              </button>
            </div>

            {showRationale && <div className="mt-8" data-id="element-487" data-mp="17hxmqi1y7p7z">
                <p className="eyebrow text-muted" data-id="element-488" data-mp="12kflldu6aeaj">Selection rationale</p>
                <div className="mt-4 border-t border-rule" data-id="element-489" data-mp="12k5rpf60bmzk">
                  <div className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] gap-3 border-b border-rule py-2.5 font-mono-num text-[10px] uppercase tracking-[0.1em] text-muted/70" data-id="element-490" data-mp="yjvq0ep8ziqi">
                    <span data-id="element-491" data-mp="os7spx60f7h4">Claimed</span>
                    <span data-id="element-492" data-mp="otaiu2vid5vz" />
                    <span data-id="element-493" data-mp="jtxwrkcgo2ug">Selected from library</span>
                  </div>
                  {selectionRationale.map((r, i) => <motion.div key={r.claimed} initial={false} animate={{
                opacity: i < rationaleStep ? 1 : 0,
                y: i < rationaleStep ? 0 : 4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] items-start gap-3 border-b border-rule/70 py-3 font-mono-num text-[11.5px] leading-relaxed" data-id="element-494" data-mp="ev35du0fh5yq" data-mpi={i}>
                      <span className={r.declined ? 'text-muted' : 'text-ink'} data-id="element-495" data-mp="30e08rcgrj0je">{r.claimed}</span>
                      <span className={r.declined ? 'text-rule' : 'text-brand'} data-id="element-496" data-mp="22qrfuwefmp7d">
                        {r.declined ? '' : '→'}
                      </span>
                      <span className={r.declined ? 'text-muted' : 'text-brand'} data-id="element-497" data-mp="2cpqoiez76kud">{r.selected}</span>
                    </motion.div>)}
                </div>
              </div>}
          </section>

          <section data-id="element-498" data-mp="1k1ulytmy5ypo">
            <div className="flex items-baseline justify-between" data-id="element-499" data-mp="1k09zlrasdm2w">
              <p className="eyebrow text-muted" data-id="element-500" data-mp="1ozmp6jt1l796">Scenario mix</p>
              <button type="button" onClick={() => {
              setEditing(!editing);
              setRefusal(null);
            }} className="font-mono-num text-[11px] uppercase tracking-[0.12em] text-brand underline decoration-rule underline-offset-4 transition-colors hover:decoration-brand" data-id="element-501" data-mp="7ybrt9cg44of">
                {editing ? 'done' : 'adjust'}
              </button>
            </div>

            <p className="mt-3 font-mono-num text-[11.5px] text-ink" data-id="element-502" data-mp="3lalvog65jqhi">{MIX_SUMMARY(slots)}</p>

            <ul className="mt-4 space-y-2" data-id="element-503" data-mp="3q9fdgksh23nh">
              {orderedSlots.map(slot => <motion.li layout transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} key={slot.id} className={`flex items-center gap-4 rounded-sm border border-rule bg-white px-4 py-3 ${slot.klass === 'CLEAN' ? 'border-l-2 border-l-brand' : ''}`} data-id="element-504" data-mp="3v9bcs6q856wp">
                  <span className="font-mono-num text-[11px] text-muted" data-id="element-505" data-mp="1opdxyd33uv8m">{slot.id}</span>
                  {editing ? <select aria-label={`Class for ${slot.id}`} value={slot.klass} onChange={e => attemptChange(slot.id, e.target.value as SlotClass)} className="rounded-sm border border-rule bg-paper px-2 py-1 font-mono-num text-[11px] uppercase tracking-wider text-ink focus:border-brand focus:outline-none" data-id="element-506" data-mp="1km7s36jwn62o">
                      {SLOT_CLASSES.map(k => <option key={k} value={k} data-id="element-507" data-mp="wz9gonan6obm">
                          {k}
                        </option>)}
                    </select> : <span className="w-[124px] shrink-0 font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-brand" data-id="element-508" data-mp="mz8umtle1u3q">
                      {slot.klass}
                    </span>}
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-509" data-mp="8d3ca68yq1ab">{slot.title}</span>
                  <span className="font-mono-num text-[11px] text-muted/80" data-id="element-510" data-mp="ddr7z584z6av">{slot.row}</span>
                </motion.li>)}
            </ul>

            <p className="mt-4 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-511" data-mp="3eidmb9czpnz">
              Scope shown, not solutions. What each scenario contains stays sealed until the record.
            </p>
            <button type="button" onClick={() => navigate('/demo/pipeline', {
            state: {
              openKey: true
            }
          })} className="mt-1.5 font-mono-num text-[11px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-512" data-mp="w3z2x01omqfz">
              Full key available after generation.
            </button>

            {refusal && <p className="mt-3 rounded-sm border-l-2 border-signal-red bg-white px-4 py-3 text-[13px] leading-relaxed text-ink" data-id="element-513" data-mp="1euql8r3ndr6i">
                {refusal}
              </p>}

            <p className="mt-4 max-w-lg text-[13px] leading-relaxed text-muted" data-id="element-514" data-mp="2uk7yvtluo4v2">
              Mix is set by the role profile. At least a third clean — that floor is what makes
              appropriate trust scoreable.
            </p>
            {editing && <button type="button" onClick={() => {
            resetSlots();
            setRefusal(null);
          }} className="mt-3 font-mono-num text-[11px] uppercase tracking-[0.12em] text-muted underline decoration-rule underline-offset-4 hover:text-ink" data-id="element-515" data-mp="2gli2yharob8a">
                reset to profile mix
              </button>}
          </section>
        </div>

        <div className="mt-12 flex justify-end" data-id="element-516" data-mp="5s44p6gos1fu">
          <button type="button" onClick={() => navigate('/demo/pipeline')} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-517" data-mp="uc83z53jr5b">
            Generate and gate
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-518" data-mp="2fi9g8zhnuky1" />
          </button>
        </div>
      </main>

      <DemoFooter data-id="element-519" data-mp="2phqjzbdcqord" />
    </div>;
}